import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material'
import { Formik } from 'formik'
import { GreenText } from 'components'
import { PersonAddressDto } from 'dtos'
import { personAddressValidationSchema } from 'dtos/personAddressDto'
import { LoadingButton } from '@mui/lab'
import { useEffect, useState } from 'react'
import * as controllers from 'controllers'
import { useFedExAddresses } from 'context'

interface AddressesAddEditDialogProps {
  initialValues: PersonAddressDto
  // isDeclarantPerson is used to determine if the "Declarant Address" address type switch should be enabled or disabled.
  isDeclarantPerson: boolean
  loading: boolean
  onClose: () => void
  onSave: (values: PersonAddressDto) => void
  open: boolean
}

export default function AddressesAddEditDialog({ initialValues, isDeclarantPerson, loading, onClose, onSave, open }: AddressesAddEditDialogProps) {
  const { countryCodes, stateProvinceCodes } = useFedExAddresses()

  const [isGettingDeclarantPersonAddress, setIsGettingDeclarantPersonAddress] = useState<boolean>(false)
  const [declarantPersonAddressAlreadyExists, setDeclarantPersonAddressAlreadyExists] = useState<boolean>(false)

  useEffect(() => {
    if (isDeclarantPerson) {
      setIsGettingDeclarantPersonAddress(true)
      controllers
        .getDeclarantPersonAddress()
        .then(response => {
          if (response.value) {
            setDeclarantPersonAddressAlreadyExists(response.value.addressGuid !== initialValues.addressGuid)
          } else {
            setDeclarantPersonAddressAlreadyExists(false)
          }
        })
        .finally(() => {
          setIsGettingDeclarantPersonAddress(false)
        })
    }
  }, [isDeclarantPerson, initialValues.addressGuid])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={personAddressValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.addressGuid ? 'Edit' : 'Add'} Address</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.addressNickname && errors.addressNickname)}
                      helperText={touched.addressNickname && errors.addressNickname}
                      label='Address Nickname'
                      name='addressNickname'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.addressNickname}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      label='Phone'
                      name='phone'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.phone}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      label='Email'
                      name='email'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.address1 && errors.address1)}
                      helperText={touched.address1 && errors.address1}
                      label='Address 1'
                      name='address1'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address1}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.address2 && errors.address2)}
                      helperText={touched.address2 && errors.address2}
                      label='Address 2'
                      name='address2'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address2}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      label='City'
                      name='city'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.city}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.county && errors.county)}
                      helperText={touched.county && errors.county}
                      label='County'
                      name='county'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.county ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={loading}
                      disableListWrap
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('stateProvince', value)

                        let country = undefined

                        if (value) {
                          let stateProvinceCode = stateProvinceCodes.filter(stateProvinceCode => stateProvinceCode.state === value)[0]
                          country = countryCodes.filter(countryCode => countryCode.code === stateProvinceCode.countryCode)[0]?.countryTerritory ?? undefined
                        }

                        setFieldValue('country', country)
                      }}
                      options={stateProvinceCodes.map(({ state }) => state)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.stateProvince && errors.stateProvince)}
                          helperText={touched.stateProvince && errors.stateProvince}
                          label='State / Province'
                        />
                      )}
                      value={values.stateProvince ?? null}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      helperText={touched.postalCode && errors.postalCode}
                      label='Postal Code'
                      name='postalCode'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.postalCode}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField disabled error={Boolean(errors.country)} helperText={errors.country} label='Country' value={values.country || ''} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          disabled={loading || isSubmitting}
                          name='isActive'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isActive}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Active</Typography>

                          <Typography variant='body2' color='#808080'>
                            Makes address active.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isHomeAddress}
                          disabled={loading || isSubmitting}
                          name='isHomeAddress'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isHomeAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Home Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Personal home address.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isWorkAddress}
                          disabled={loading || isSubmitting}
                          name='isWorkAddress'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isWorkAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Work Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            This may be the same or different as the company address if associated to a company.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isDeclarantAddress}
                          disabled={loading || isSubmitting || !isDeclarantPerson || isGettingDeclarantPersonAddress || declarantPersonAddressAlreadyExists}
                          name='isDeclarantAddress'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isDeclarantAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Declarant Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Only one Declarant address can exist in the system.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isTitleClerkAddress}
                          disabled={loading || isSubmitting}
                          name='isTitleClerkAddress'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isTitleClerkAddress}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Title Clerk Address</Typography>

                          <Typography variant='body2' color='#808080'>
                            Used for Title Application Shipping Labels.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Address Nickname:**

Required free-text field.

**Phone Number:**

Numeric-only free-text field.

**Email:**

Free-text field for entering an email address.

**Address 1:**

Free-text field for the primary address line.

**Address 2:**

Free-text field for the secondary address line.

**City:**

Free-text field for the city name.

**County:**

Optional free-text field for the county name.

**State/Province:**

Free-text field for the state or province.

**Postal Code:**

Free-text field for the postal or ZIP code.

**Country:**

Free-text field for the country name.

**Active Switch:**

Makes address active

**Home Address Switch:**

Indicates the person's home address.

**Work Address Switch:**

Indicates the person's work address.

**Declarant Address:**

Indicates the declarant address.

**Title Clerk Address Switch:**

Used for Title Application Shipping Labels.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.

`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={loading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
